// Helpers for restricting input on numeric TextFields.
//
// Firefox's `<input type="number">` does not block non-numeric characters
// (letters like `e`/`E`, signs `+`/`-`, or other special characters) the way
// Chromium does. These helpers sanitize keyboard and paste input so only
// digits (and optionally a single decimal separator) reach the field.

const ALLOWED_CONTROL_KEYS = new Set([
  'Backspace',
  'Tab',
  'Enter',
  'Escape',
  'Delete',
  'Home',
  'End',
  'ArrowLeft',
  'ArrowRight',
  'ArrowUp',
  'ArrowDown',
]);

import type { KeyboardEventHandler } from 'react';

type NumericInputOptions = {
  allowDecimal?: boolean;
};

const buildCharRegex = (allowDecimal: boolean): RegExp => (allowDecimal ? /^[0-9.]$/ : /^[0-9]$/);

const buildValueRegex = (allowDecimal: boolean): RegExp => (allowDecimal ? /[^0-9.]/g : /[^0-9]/g);

export const makeNumericKeyDownHandler =
  ({ allowDecimal = true }: NumericInputOptions = {}) =>
  (event: Parameters<KeyboardEventHandler<HTMLDivElement>>[0]): void => {
    if (event.ctrlKey || event.metaKey || event.altKey) return;
    if (ALLOWED_CONTROL_KEYS.has(event.key)) return;

    const charRegex = buildCharRegex(allowDecimal);
    if (!charRegex.test(event.key)) {
      event.preventDefault();
      return;
    }

    const { target } = event;
    const currentValue =
      target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement ? target.value : '';

    if (allowDecimal && event.key === '.' && currentValue.includes('.')) {
      event.preventDefault();
    }
  };

export const sanitizeNumericValue = (
  value: string | number | null | undefined,
  { allowDecimal = true }: NumericInputOptions = {},
): string => {
  if (value == null) return '';
  let sanitized = String(value).replace(buildValueRegex(allowDecimal), '');
  if (allowDecimal) {
    const firstDot = sanitized.indexOf('.');
    if (firstDot !== -1) {
      sanitized = `${sanitized.slice(0, firstDot + 1)}${sanitized.slice(firstDot + 1).replace(/\./g, '')}`;
    }
  }
  return sanitized;
};
